/**
 * SOMEBODY WHO DOES TWO JOBS HAS TWO THINGS TO LEARN.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WHAT WAS WRONG
 * ═══════════════════════════════════════════════════════════════════════════
 * `resolveOnboardingExperience` maps ONE scalar role to ONE walkthrough,
 * because until now a person had one role. Andy writes repair orders in the
 * morning and turns wrenches in the afternoon; under the scalar he was one or
 * the other, and whichever he was, half his job went untaught.
 *
 * It is not enough to concatenate the two step libraries into one long tour.
 * `onboarding_state` is keyed by (user, shop, flow), and a merged flow would
 * be a new durable identity: finishing the technician half and stopping would
 * record "incomplete" forever, and there would be no way to say "I know the
 * board, show me the timer".
 *
 * So the walkthroughs stay separate and the PERSON gets several of them. Each
 * keeps its own row, its own progress, and its own completion — which is what
 * "independently" means.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ACCESS IS TAUGHT SEPARATELY FROM WORK
 * ═══════════════════════════════════════════════════════════════════════════
 * A manager who also details needs the manager walkthrough AND the detail one,
 * and neither implies the other. Authority contributes at most one experience,
 * each work role contributes at most one, and the same experience arriving
 * from two directions appears once.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * A PERSON WITH NO WORK ROLES IS NOT A PERSON WITH NOTHING TO LEARN
 * ═══════════════════════════════════════════════════════════════════════════
 * An owner who performs no vehicle work still runs the shop. When the work-set
 * is empty the authority experience stands alone, and when BOTH are empty this
 * falls back to the legacy scalar resolution rather than returning nothing —
 * the same asymmetry the rest of the multi-role bridge uses, for the same
 * reason: `[]` is an answer and a missing set is not.
 */
import {
  EXPERIENCE_FOR_ROLE,
  resolveOnboardingExperience,
  type AppRole,
  type KnownAppRole,
  type ExperienceResolution,
  type MembershipResolution,
  type OnboardingExperience,
} from './onboardingExperience';
import { WORK_ROLES, type AuthorityRole, type WorkRole } from '@/lib/roles/multiRole';

/**
 * What each work role has to be taught.
 *
 * DETAIL_SPECIALIST gets its OWN track (`detail_core_v1`, Core-100 R4, OD-14),
 * never the technician one: the technician track teaches mechanical job flow
 * a detailer may not take, which the assignment authority refuses. Until R4
 * this was `null` — visible, but one of the five Core roles had nothing.
 *
 * DISPATCHER and PARTS keep their keys (OD-14: identifiers are kept), but no
 * NEW onboarding experience is built for them: Parts staffing is OptiShop and
 * dispatch is handled by advisor/manager/owner permissions.
 */
export const EXPERIENCE_FOR_WORK_ROLE: Readonly<
  Record<WorkRole, OnboardingExperience | null>
> = Object.freeze({
  TECHNICIAN: 'technician_core_v1',
  SERVICE_ADVISOR: 'foh_service_writer',
  DISPATCHER: 'foh_dispatcher',
  PARTS: 'parts_core_v1',
  DETAIL_SPECIALIST: 'detail_core_v1',
});

/**
 * What each authority level has to be taught, as distinct from any job.
 *
 * STAFF contributes nothing: "may view the shop" is not a curriculum, and
 * emitting an orientation tour for it would give every technician a second
 * walkthrough nobody asked for.
 */
export const EXPERIENCE_FOR_AUTHORITY: Readonly<
  Record<AuthorityRole, OnboardingExperience | null>
> = Object.freeze({
  STAFF: null,
  AUDITOR: EXPERIENCE_FOR_ROLE.AUDITOR,
  MANAGER: EXPERIENCE_FOR_ROLE.MANAGER,
  DISTRICT_MANAGER: EXPERIENCE_FOR_ROLE.DISTRICT_MANAGER,
  REGION_MANAGER: EXPERIENCE_FOR_ROLE.REGION_MANAGER,
  ORG_ADMIN: EXPERIENCE_FOR_ROLE.ORG_ADMIN,
  OWNER: EXPERIENCE_FOR_ROLE.OWNER,
});

/** Core-100 R4 W5 (CORE-BL-GAP-011): offered to OWNER / MANAGER authority, next to `manager`. */
export const MANAGER_OPERATIONS_EXPERIENCE: OnboardingExperience = 'manager_ops_v1';

export interface MultiRoleExperienceInput {
  /** The legacy resolution, still the authority when nothing else is known. */
  membership: MembershipResolution;
  authority?: AuthorityRole | null;
  /**
   * The person's work roles. `null`/`undefined` means "not loaded or not
   * recorded" and falls back to the scalar; `[]` means "performs no vehicle
   * work" and is respected.
   */
  workRoles?: readonly WorkRole[] | null;
}

export interface MultiRoleExperiences {
  /** Every walkthrough this person should be offered, in a stable order. */
  experiences: OnboardingExperience[];
  /**
   * The one to open when nobody has chosen. First in the list; the work
   * comes before the administration, because somebody arriving for their
   * first shift needs to know how to do the job before how to configure it.
   */
  primary: OnboardingExperience | null;
  /**
   * Work roles this person holds that have no walkthrough written. Surfaced
   * so the gap is stated rather than looking like completeness.
   */
  untaughtWorkRoles: WorkRole[];
  /** The legacy single-experience resolution, unchanged, for every old caller. */
  legacy: ExperienceResolution;
}

/**
 * Every walkthrough a person should be offered, given what they do.
 *
 * Order is WORK first, in the canonical `WORK_ROLES` order, then authority.
 * It is stable rather than alphabetical or arrival-ordered so that the menu
 * does not reshuffle itself between loads.
 */
export function resolveMultiRoleExperiences(
  input: MultiRoleExperienceInput,
): MultiRoleExperiences {
  const legacy = resolveOnboardingExperience(input.membership);

  // Nothing resolved at all — loading, no membership, a failed read. There is
  // no list to build and the reason belongs to the caller to render.
  if (legacy.status !== 'experience') {
    return { experiences: [], primary: null, untaughtWorkRoles: [], legacy };
  }

  const ordered: OnboardingExperience[] = [];
  const seen = new Set<OnboardingExperience>();
  const add = (e: OnboardingExperience | null) => {
    if (e && !seen.has(e)) { seen.add(e); ordered.push(e); }
  };

  const untaughtWorkRoles: WorkRole[] = [];

  // WORK — in canonical order, so the menu is stable.
  const held = input.workRoles;
  if (held != null) {
    for (const role of WORK_ROLES) {
      if (!held.includes(role)) continue;
      const experience = EXPERIENCE_FOR_WORK_ROLE[role];
      if (experience === null) untaughtWorkRoles.push(role);
      add(experience);
    }
  }

  // ACCESS — separately, and never implied by the work above.
  if (input.authority) {
    add(EXPERIENCE_FOR_AUTHORITY[input.authority]);
    // Core-100 R4 W5 (CORE-BL-GAP-011): an owner or manager is also offered the day-to-day operations walkthrough —
    // AFTER the setup one, which stays the walkthrough that opens (`chooseActiveExperience` keeps the legacy
    // `manager`), so nobody's existing progress changes meaning.
    if (input.authority === 'OWNER' || input.authority === 'MANAGER') add(MANAGER_OPERATIONS_EXPERIENCE);
  }

  // The bridge. A membership nobody has described in the new model still gets
  // exactly what it got before — including when its work-set is genuinely
  // empty and its authority teaches nothing, which is a real state (a STAFF
  // row with no jobs) and must not be an empty menu.
  if (ordered.length === 0) add(legacy.experience);

  return {
    experiences: ordered,
    primary: ordered[0] ?? null,
    untaughtWorkRoles,
    legacy,
  };
}

/**
 * Is this experience one the person is actually entitled to?
 *
 * The single-experience version of this check is what stopped a technician
 * being offered — and durably recorded against — a manager walkthrough that
 * was never theirs. Multi-role widens the set; it must not remove the check.
 */
export function isOfferedExperience(
  resolved: MultiRoleExperiences,
  experience: OnboardingExperience,
): boolean {
  return resolved.experiences.includes(experience);
}

/**
 * Core-100 R4 CORE-BL — WHICH walkthrough runs, for a person the multi-role
 * model has described.
 *
 * The runtime used to run the LEGACY scalar's experience only. A pure Detail
 * Specialist is the neutral STAFF on the scalar, so they were put on the
 * read-only track — "nothing here will ask you to alter a record" — while
 * their job is running timers; and its pages are closed to STAFF, so in
 * practice they got no walkthrough at all. A STAFF member who turns wrenches
 * had the same problem.
 *
 * Rule: the legacy experience stays in force when it is one the person is
 * actually offered (nobody's existing progress row changes meaning: an owner
 * who also turns wrenches still opens on `manager`); otherwise the first
 * offered experience (work before administration) runs. The other offered
 * experiences remain available to switch to (`TutorialContext.switchExperience`).
 */
export function chooseActiveExperience(
  multi: MultiRoleExperiences,
): OnboardingExperience | null {
  const legacy = multi.legacy.status === 'experience' ? multi.legacy.experience : null;
  if (legacy && multi.experiences.includes(legacy)) return legacy;
  return multi.primary ?? legacy;
}

/** The scalar roles that no longer decide the walkthrough on their own. */
export type { AppRole, KnownAppRole };
